import { formatCompactDurationSeconds, ONE_SECOND } from '@air/utilities';

import { type WorkspaceAgentRunOutcome } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentRun';

const MS_PER_SECOND = ONE_SECOND;

const getDurationSeconds = ({ finishedAt, startedAt }: { finishedAt?: string; startedAt?: string }): number | null => {
  if (!startedAt || !finishedAt) {
    return null;
  }

  const durationMs = Date.parse(finishedAt) - Date.parse(startedAt);

  if (!Number.isFinite(durationMs) || durationMs < 0) {
    return null;
  }

  return Math.max(1, Math.round(durationMs / MS_PER_SECOND));
};

const formatFailedStepSuffix = (failedStepCount: number): string | null => {
  if (failedStepCount <= 0) {
    return null;
  }

  if (failedStepCount === 1) {
    return '1 step failed';
  }

  return `${failedStepCount} steps failed`;
};

export const formatWorkspaceAgentRunSettledLabel = ({
  failedStepCount = 0,
  finishedAt,
  outcome,
  startedAt,
}: {
  failedStepCount?: number;
  finishedAt?: string;
  outcome?: WorkspaceAgentRunOutcome;
  startedAt?: string;
}): string => {
  const durationSeconds = getDurationSeconds({ finishedAt, startedAt });
  const durationLabel = durationSeconds !== null ? formatCompactDurationSeconds(durationSeconds) : null;
  const failedSuffix = formatFailedStepSuffix(failedStepCount);

  if (outcome?.type === 'cancelled') {
    if (durationLabel !== null) {
      return `Stopped after ${durationLabel}`;
    }

    return 'Stopped';
  }

  if (outcome?.type === 'error') {
    if (durationLabel !== null) {
      return `Failed after ${durationLabel}`;
    }

    return 'Failed';
  }

  const workedBase = durationLabel !== null ? `Worked for ${durationLabel}` : 'Worked';

  if (failedSuffix) {
    return `${workedBase} · ${failedSuffix}`;
  }

  return workedBase;
};
